import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Play } from "@phosphor-icons/react";
import Brand from "../../../../components/Brand/Brand";
import { parseClients, presenceFor } from "../../presence";
import useReveal from "./useReveal";
import { goldAt, seenBy } from "./reveal";
import Figures from "../../Figures";
import ClientsInputV2 from "../ClientsInputV2";
import { BREATHS } from "./breaths";
import { useHomeLang } from "../../homeLang";
import "../heroV2.css";
import "./shapes.css";

// PROTOTYPE (hero shapes): the new hero's pieces round its clients
// (DiscHero.jsx): the headline with its field, the count and the close.
//
// The hero never holds the page back: it scrolls the moment it is asked
// to. Its reveal plays when the visitor asks for it, with the close's
// button, and never by itself (useReveal.js). It plays in two breaths
// (breaths.js): as the disc fills
// (reveal.js), the count stays on the clients you meet while they turn
// gold, then climbs to all of them. It turns gold, and the caption tells
// AVRAH's ending, once half the clients you meet are gold. It plays once:
// back at the top it stays played.
export function useShapeStory(rawClients) {
  const { pace, actsOf } = BREATHS;
  const { reveal, play, played } = useReveal(pace);
  const clients = parseClients(rawClients);
  const { met, others } = presenceFor(clients);
  const { fill, morph } = actsOf(reveal);
  const gold = goldAt({ clients, met }, fill);
  return {
    reveal,
    clients,
    met,
    others,
    fill,
    morph,
    gold,
    count: seenBy(met, gold),
    lit: gold >= met / 2,
    asked: played,
    started: reveal > 0,
    done: reveal >= 1,
    start: play,
  };
}

// The shell.
export function ShapeStage({ story, shape, children }) {
  return (
    <div className="hv">
      <div className="hv-inner">
        <section aria-labelledby="hf-h1" className="hs-grid" data-shape={shape}>
          {children}
        </section>
      </div>
    </div>
  );
}

// The hero's words. The headline wraps the visitor's number, `field`, kept on
// one line with the word after it.
const WORDS = {
  it: {
    said: (field) => <>Ti hanno scelto <span className="hv-keep">{field} clienti.</span></>,
    ask: "Quanti ti vedono davvero?",
    play: "E con A.V.R.A.H.?",
    try: "Prova il tuo video",
  },
  en: {
    said: (field) => <><span className="hv-keep">{field} clients</span> chose you.</>,
    ask: "How many really see you?",
    play: "And with A.V.R.A.H.?",
    try: "Try your own video",
  },
};

export function Headline({ rawClients, onClients }) {
  const words = WORDS[useHomeLang()];
  return (
    <h1 id="hf-h1" className="hs-h1">
      <span className="hs-said">{words.said(<ClientsInputV2 value={rawClients} onChange={onClients} />)}</span>{" "}
      <span className="hs-ask">{words.ask}</span>
    </h1>
  );
}

// The count. It keeps the width of its most digits, so it never moves what
// is around it. `countRef` reads the room it takes.
export function Hub({ story, countRef, ...rest }) {
  const { clients, count, lit } = story;
  return (
    <div className="hs-hub" style={{ "--digits": String(clients).length }} {...rest}>
      <span className="hv-count" ref={countRef} data-lit={lit} aria-hidden="true">
        <Figures>{count}</Figures>
      </span>
    </div>
  );
}

// Until the story starts, the button that plays it, gold as the reveal it
// starts; while `invited.button` says how, it answers the hero's invitation
// to press it (invite.js), and `invited.watch` is told where it is. Once
// every client is lit, in its place, the link to try a video, in white, its
// words beside a play button, so gold stays the trial's. A visitor who
// played it from the button, and whose focus went with it, finds their
// focus on the link.
export function Close({ story, invited }) {
  const words = WORDS[useHomeLang()];
  const playRef = useRef(null);
  const tryRef = useRef(null);
  const askedRef = useRef(false);
  const { watch, button } = invited;
  const placePlay = useCallback(
    (element) => {
      playRef.current = element;
      watch(element);
    },
    [watch],
  );
  useEffect(() => {
    if (!story.done || !askedRef.current) return;
    const focused = document.activeElement;
    if (focused === playRef.current || focused === document.body) tryRef.current.focus({ preventScroll: true });
  }, [story.done]);
  const play = () => {
    askedRef.current = true;
    story.start();
  };
  return (
    <div className="hs-end">
      <button
        type="button"
        ref={placePlay}
        className="btn btn-gold hv-cta"
        data-shown={!story.started}
        data-inviting={button ? "" : undefined}
        style={button ? { "--invite-light": button.light, "--invite-scale": button.scale } : undefined}
        onClick={play}
      >
        {/* One run of text, so the button's flex gap stays out of it. */}
        <span>
          <Brand>{words.play}</Brand>
        </span>
      </button>
      <a ref={tryRef} className="hv-cta hv-try" href="#prova-video" data-shown={story.done}>
        <span className="hv-try-play" aria-hidden="true">
          <Play weight="fill" />
        </span>
        <span>{words.try}</span>
      </a>
    </div>
  );
}

// An element's size, kept up to date; null until it has one. Returns a
// callback ref, so it follows the element when it is swapped for another.
// The size is as laid out, before any transform: one applied once the size
// is known (Costellazione's count) does not change it.
export function useBox() {
  const [element, setElement] = useState(null);
  const [box, setBox] = useState(null);
  useLayoutEffect(() => {
    if (!element) {
      setBox(null);
      return undefined;
    }
    const keep = (width, height) => setBox((last) => (width === 0 ? null : last && last.width === width && last.height === height ? last : { width, height }));
    const { width, height } = element.getBoundingClientRect();
    keep(width, height);
    const observer = new ResizeObserver(([entry]) => {
      const [{ inlineSize, blockSize }] = entry.borderBoxSize;
      keep(inlineSize, blockSize);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);
  return [setElement, box];
}
